// LDG-1.3: the one declared way to represent, combine and round money in Joules.
// Pure, no React, no IO, unit-tested in isolation (money.test.ts).
//
// ── THE DECISION, AND WHY ────────────────────────────────────────────────────
// Money is represented as EXACT BASE-10 FIXED POINT: a bigint of units plus a
// scale, where the value is `units / 10^scale`. It is never a JS `number` at any
// point where a cent can be created or destroyed.
//
// The alternative that was already in the codebase is `parseFloat` + `toFixed(2)`,
// in computeFinance.ts. That was CORRECT for what FIN-1.7 needed — reproducing a
// live display byte for byte — and is wrong for money that gets paid, for three
// reasons that do not depend on how much data exists:
//
//   1 · A binary double cannot hold 0.1, so `0.1 + 0.2 === 0.30000000000000004`.
//       Commission is a chain of multiplications and subtractions over rates like
//       0.15; the error accumulates along the chain, it does not cancel.
//   2 · `toFixed` does not round half up. It rounds the BINARY value, so
//       `(1.005).toFixed(2)` is "1.00" and `(8.575).toFixed(2)` is "8.57". A pay
//       statement that is a cent light on ties is a pay statement a rep disputes.
//   3 · `toFixed` returns a string, so every downstream step re-parses it. That
//       makes every step a rounding step, which is exactly what Commission NFR-1
//       forbids: rounding happens ONCE, at line creation.
//
// ⚠️ MEASURED 2026-08-19, and recorded so nobody mistakes this for a live defect:
// across the 1,695 live projects with an `estimated_system_size`, the float path
// and exact arithmetic agree on BOTH per-watt formulas for every single row, and
// no row's exact result even has a third decimal place. Today's two formulas are
// `rate x size x 1000`, which is float-safe by luck of the inputs. COM-1 onward
// multiplies by percentage splits and divides a pool between contributors, which
// is where ties and residue appear. This story is a foundation, not a fix.
//
// ── THE ROUNDING RULE ────────────────────────────────────────────────────────
// HALF UP, AWAY FROM ZERO, at 2 decimal places, applied exactly once, at the
// moment a payable line is created. See `roundToMoney`.
//
// Chosen over banker's rounding for one decisive reason: Postgres `round(numeric, 2)`
// is half-away-from-zero, and money in this system is `numeric` in the database
// (measured 2026-08-19: all 56 monetary columns over 23 tables are `numeric`; zero
// are float). A trigger that computes a line in SQL and a preview that computes it
// in the browser must produce the same cent, and matching the database's own rule
// is the only way to get that without wrapping every SQL rounding call in a custom
// function. Banker's rounding would be defensible statistically and indefensible
// operationally: it would put the two tiers a cent apart on every tie.
//
// ── WHAT THIS MODULE DOES NOT DO ─────────────────────────────────────────────
// No currency tag. Joules is USD-only, and a currency field nothing reads is a lie
// waiting to be believed. Add it when a second currency exists, not before.

/**
 * An exact base-10 fixed-point number: `units / 10^scale`.
 *
 * ⚠️ ALWAYS CANONICAL. Trailing zeros are stripped on construction and after every
 * operation, and zero is always `{units: 0n, scale: 0}`. Two Decimals are equal as
 * values if and only if they are structurally equal, which is what makes
 * Commission NFR-4 ("the same inputs produce byte-identical results") checkable
 * with a plain deep-equal rather than with a tolerance.
 */
export interface Decimal {
  readonly units: bigint;
  readonly scale: number;
}

/** Money is carried at 2 decimal places. The rounding rule below is what puts it there. */
export const MONEY_SCALE = 2;

/**
 * The declared rule, as a string, so a UI or an audit row can state it rather than
 * imply it. The `inputs` provenance on a ledger line should carry this verbatim.
 */
export const MONEY_ROUNDING_RULE =
  'half up, away from zero, to 2 decimal places, applied once at line creation';

const TEN = 10n;

function pow10(n: number): bigint {
  return TEN ** BigInt(n);
}

/** Strip trailing zeros so the representation is canonical. */
function canonical(units: bigint, scale: number): Decimal {
  if (units === 0n) return { units: 0n, scale: 0 };
  let u = units;
  let s = scale;
  while (s > 0 && u % TEN === 0n) {
    u /= TEN;
    s -= 1;
  }
  return { units: u, scale: s };
}

export const ZERO: Decimal = { units: 0n, scale: 0 };

function isDecimal(v: unknown): v is Decimal {
  return typeof v === 'object' && v !== null && typeof (v as Decimal).units === 'bigint';
}

// Sign, digits, optional fraction, optional exponent. Deliberately strict: no
// thousands separators, no currency symbols, no internal whitespace, no bare ".5".
// `parseFloat` accepted "2.3abc" as 2.3, which is how a truncated paste becomes a
// number nobody typed.
const DECIMAL_RE = /^[+-]?\d+(\.\d+)?([eE][+-]?\d+)?$/;

function fromString(raw: string): Decimal | null {
  const text = raw.trim();
  if (text === '' || !DECIMAL_RE.test(text)) return null;

  const [mantissa, exponentPart] = text.split(/[eE]/);
  const exponent = exponentPart === undefined ? 0 : Number(exponentPart);

  // W1R P10: "1e999999999" matches the grammar, and materialising its zeros
  // means 10n ** 999999999n — a RangeError that crashed the caller instead of
  // the null this function promises for unusable input. No money-adjacent
  // value has an exponent anywhere near ±400 (a double tops out at e308).
  if (Math.abs(exponent) > 400) return null;
  const negative = mantissa.startsWith('-');
  const unsigned = mantissa.replace(/^[+-]/, '');
  const [intPart, fracPart = ''] = unsigned.split('.');

  let units = BigInt(intPart + fracPart);
  if (negative) units = -units;
  let scale = fracPart.length - exponent;

  // A negative scale means the exponent implies trailing zeros; materialise them
  // rather than carrying a negative scale through every other function.
  if (scale < 0) {
    units *= pow10(-scale);
    scale = 0;
  }
  return canonical(units, scale);
}

/**
 * Parse anything that might be a number into an exact Decimal, or null.
 *
 * This is the replacement for the `num()` helper in computeFinance.ts, and one
 * behaviour change is deliberate: `parseFloat` returned 2.3 for the string
 * "2.3abc"; this returns null.
 *
 * ⚠️ A JS `number` input is exact only as far as its own shortest round-trip
 * literal: `decimal(0.1 + 0.2)` is 0.30000000000000004, honestly. Values that come
 * from the database arrive as STRINGS (supabase-js returns `numeric` as a string
 * precisely so precision survives the wire) and must be passed through as strings.
 */
export function tryDecimal(value: unknown): Decimal | null {
  if (value == null) return null;
  if (isDecimal(value)) return value;
  if (typeof value === 'bigint') return canonical(value, 0);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return fromString(String(value));
  }
  if (typeof value === 'string') return fromString(value);
  return null;
}

/** `tryDecimal` for values that must parse. Throws rather than inventing a zero. */
export function decimal(value: string | number | bigint | Decimal): Decimal {
  const d = tryDecimal(value);
  if (d === null) throw new TypeError(`Not a decimal: ${JSON.stringify(value)}`);
  return d;
}

/** Bring two Decimals to a common scale so they can be added or compared. */
function align(a: Decimal, b: Decimal): { a: bigint; b: bigint; scale: number } {
  const scale = Math.max(a.scale, b.scale);
  return {
    a: a.units * pow10(scale - a.scale),
    b: b.units * pow10(scale - b.scale),
    scale,
  };
}

/** Exact. Never rounds. */
export function add(a: Decimal, b: Decimal): Decimal {
  const x = align(a, b);
  return canonical(x.a + x.b, x.scale);
}

/** Exact. Never rounds. */
export function subtract(a: Decimal, b: Decimal): Decimal {
  const x = align(a, b);
  return canonical(x.a - x.b, x.scale);
}

/**
 * Exact. Never rounds: scales add, so the product carries every digit it needs.
 * This is the whole reason intermediate steps can be required not to round.
 */
export function multiply(a: Decimal, b: Decimal): Decimal {
  return canonical(a.units * b.units, a.scale + b.scale);
}

export function negate(a: Decimal): Decimal {
  return canonical(-a.units, a.scale);
}

export function isZero(a: Decimal): boolean {
  return a.units === 0n;
}

export function isNegative(a: Decimal): boolean {
  return a.units < 0n;
}

/** -1, 0 or 1. */
export function compare(a: Decimal, b: Decimal): -1 | 0 | 1 {
  const x = align(a, b);
  return x.a < x.b ? -1 : x.a > x.b ? 1 : 0;
}

export function equals(a: Decimal, b: Decimal): boolean {
  return compare(a, b) === 0;
}

/**
 * Divide by a power of ten. EXACT, always, because it is a scale shift and not a
 * division at all. Use this rather than `divide` whenever the divisor is 10, 100,
 * 1000 … — which covers per-watt and per-kW conversions, the only divisions the
 * finance view currently performs.
 */
export function divideByPowerOfTen(a: Decimal, exponent: number): Decimal {
  if (!Number.isInteger(exponent) || exponent < 0) {
    throw new RangeError(`divideByPowerOfTen expects a non-negative integer exponent, got ${exponent}`);
  }
  return canonical(a.units, a.scale + exponent);
}

/**
 * The ONE operation here that can round, because a quotient like 1/3 has no exact
 * decimal form. The result scale is therefore explicit at every call site.
 *
 * ⚠️ DOUBLE ROUNDING. A quotient rounded to `scale` and then rounded again to 2
 * places can differ by a cent from the same quotient rounded straight to 2 places.
 * That is not a bug here, it is the reason NFR-1 says rounding happens ONCE: divide
 * into a payable amount and round that amount at line creation, do not round the
 * quotient on the way. The generous default scale makes accidental double rounding
 * vanishingly unlikely; it does not make it impossible, and nothing can.
 */
export function divide(a: Decimal, b: Decimal, opts: { scale?: number } = {}): Decimal {
  if (isZero(b)) throw new RangeError('Division by zero');
  const scale = opts.scale ?? 20;
  if (!Number.isInteger(scale) || scale < 0) {
    throw new RangeError(`divide expects a non-negative integer scale, got ${scale}`);
  }
  // Shift the numerator so the truncated quotient carries `scale` digits plus one
  // extra digit, which is what decides the rounding.
  //
  // The negative-shift branch truncates the numerator BEFORE dividing, which a
  // W1R review finding flagged as losing the deciding digits. It does not:
  // trunc(trunc(a/m)/b) = trunc(a/(m*b)) for m > 0 — the nested truncated
  // division identity, which holds for toward-zero truncation in every sign
  // case — so the guard digit is identical either way.
  const shift = scale - a.scale + b.scale + 1;
  const numerator = shift >= 0 ? a.units * pow10(shift) : a.units / pow10(-shift);
  const extended = numerator / b.units;
  return roundUnits(extended, scale + 1, scale);
}

/** Half up, away from zero, from `fromScale` down to `toScale`. */
function roundUnits(units: bigint, fromScale: number, toScale: number): Decimal {
  if (toScale >= fromScale) return canonical(units * pow10(toScale - fromScale), toScale);
  const divisor = pow10(fromScale - toScale);
  const quotient = units / divisor; // bigint division truncates toward zero
  const remainder = units % divisor; // and the remainder carries the numerator's sign
  const magnitude = remainder < 0n ? -remainder : remainder;
  if (magnitude * 2n >= divisor) {
    return canonical(quotient + (units < 0n ? -1n : 1n), toScale);
  }
  return canonical(quotient, toScale);
}

/**
 * Round to `places` decimals under the declared rule: half up, away from zero.
 * `roundTo(decimal('1.005'), 2)` is 1.01, where `(1.005).toFixed(2)` is "1.00".
 */
export function roundTo(a: Decimal, places: number): Decimal {
  if (!Number.isInteger(places) || places < 0) {
    throw new RangeError(`roundTo expects a non-negative integer places, got ${places}`);
  }
  return roundUnits(a.units, a.scale, places);
}

/**
 * THE SINGLE ROUNDING SITE FOR PAYABLE MONEY. Call this once, when a line is
 * created, on the final amount — never on an input and never on an intermediate.
 */
export function roundToMoney(a: Decimal): Decimal {
  return roundTo(a, MONEY_SCALE);
}

/** Full-precision decimal string. No exponent, no loss, canonical. */
export function toDecimalString(a: Decimal): string {
  return toFixedString(a, a.scale);
}

/**
 * The value as a string with exactly `places` decimals, rounded once under the
 * declared rule. This is the replacement for `toFixed`, and unlike `toFixed` it
 * never consults a binary double.
 */
export function toFixedString(a: Decimal, places: number): string {
  const rounded = roundTo(a, places);
  const units = rounded.units * pow10(places - rounded.scale);
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(places + 1, '0');
  const body =
    places === 0
      ? digits
      : `${digits.slice(0, digits.length - places)}.${digits.slice(digits.length - places)}`;
  return negative ? `-${body}` : body;
}

/**
 * A money amount as a bare 2-decimal string: `4300.00`, `-500.00`.
 *
 * ⚠️ THIS IS THE WIRE/DEBUG FORM, NOT THE FORM TO SHOW A PERSON.
 * It has no currency symbol and no digit grouping, so `1234567.00` is a wall of
 * digits an operator has to count. Use `formatMoneyDisplay()` for anything a
 * user reads. This one is kept because a good many tests and internal strings
 * assert on the exact digits, and because a caller that supplies its own `$`
 * needs the unprefixed value.
 */
export function formatMoney(a: Decimal): string {
  return toFixedString(a, MONEY_SCALE);
}

/**
 * The product's one currency symbol.
 *
 * ⚠️ HARD-CODED, LIKE THE REST OF THE PRODUCT, AND THAT IS A KNOWN LIMIT RATHER
 * THAN AN OVERSIGHT. Every other money surface here builds
 * `Intl.NumberFormat('en-US', { currency: 'USD' })` inline; there is no tenant
 * currency setting anywhere in the schema. When one arrives this constant and
 * `formatMoneyDisplay()` are the single place it has to reach, which is the
 * reason they exist as a named pair instead of another inline `Intl` call.
 */
export const MONEY_CURRENCY_SYMBOL = '$';

/**
 * A money amount as a person reads it: `$4,300.00`, `-$500.00`, `$0.00`.
 *
 * ⚠️ IT NEVER TOUCHES A JS NUMBER, WHICH IS WHY IT IS NOT `Intl.NumberFormat`.
 * `Intl` takes a `number`, so reaching it means `toNumber()`, and this whole
 * module exists to keep money off the binary-double path. Grouping is applied
 * to the exact digit STRING that `toFixedString` already produced. The output
 * matches what `Intl.NumberFormat('en-US', { style: 'currency', currency:
 * 'USD' })` would give for every value a `numeric` column can hold, including
 * the ones a double would round.
 *
 * ⚠️ THE MINUS GOES OUTSIDE THE SYMBOL (`-$500.00`), matching `Intl` and the
 * rest of the product. `$-500.00` is a different convention and mixing the two
 * across screens is exactly the drift this function was added to end.
 */
export interface MoneyDisplayOptions {
  /**
   * Exactly this many decimals. Shorthand for `minPlaces === maxPlaces`.
   * Defaults to 2, which is what every commission and cost surface wants.
   * Dashboard tiles and pipeline stage totals pass 0 deliberately: a headline
   * figure of `$1,284,000` is easier to compare at a glance than
   * `$1,284,000.00`, and the cents are available on the row it drills into.
   */
  places?: number;
  /** At least this many decimals. Defaults to `places`. */
  minPlaces?: number;
  /**
   * At most this many. Defaults to `places`. Trailing zeros between `minPlaces`
   * and `maxPlaces` are dropped, matching `Intl`'s min/max behaviour, which is
   * what `utils/formatters.ts` has always done for the pipeline table.
   */
  maxPlaces?: number;
}

export function formatMoneyDisplay(a: Decimal, opts: MoneyDisplayOptions = {}): string {
  const fixed = opts.places ?? MONEY_SCALE;
  const max = opts.maxPlaces ?? fixed;
  const min = Math.min(opts.minPlaces ?? fixed, max);

  const rendered = toFixedString(a, max);
  const negative = rendered.startsWith('-');
  let body = negative ? rendered.slice(1) : rendered;

  // Drop trailing zeros down to `min`, never past the decimal point itself.
  if (max > min) {
    const dot = body.indexOf('.');
    if (dot !== -1) {
      let end = body.length;
      while (end - dot - 1 > min && body[end - 1] === '0') end--;
      if (end - dot - 1 === 0) end = dot;
      body = body.slice(0, end);
    }
  }

  const dot = body.indexOf('.');
  const whole = dot === -1 ? body : body.slice(0, dot);
  const frac = dot === -1 ? '' : body.slice(dot);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${MONEY_CURRENCY_SYMBOL}${grouped}${frac}`;
}

/**
 * What to send to Postgres for a `numeric` column: the exact decimal STRING.
 *
 * ⚠️ Never send a JS number for money. supabase-js serialises it through a double,
 * which is the float path this module exists to keep out of the ledger, and
 * `numeric` will faithfully store the error.
 */
export function toDbNumeric(a: Decimal): string {
  return toDecimalString(a);
}

/**
 * ⚠️ LOSSY, AND DELIBERATELY AWKWARD TO REACH FOR. A double cannot hold every
 * decimal, so this is for charting, sorting and layout only. If the value is about
 * to be stored, compared for equality, or paid to somebody, use `toDbNumeric`,
 * `formatMoney` or `compare` instead.
 */
export function toNumber(a: Decimal): number {
  return Number(toDecimalString(a));
}
